<template>
  <div class="employee-payments-page" data-testid="employee-payments-page">
    <div class="page-header">
      <h1>{{ t('Meus pagamentos') }}</h1>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards p-mb-3">
      <box class="summary-card">
        <div class="card-content">
          <div class="icon-container" style="background-color: var(--blue-100)">
            <i class="pi pi-calendar" style="color: var(--blue-500)"></i>
          </div>
          <div class="card-details">
            <span class="card-label">{{ t('Total de períodos') }}</span>
            <span class="card-value" data-testid="employee-payments-total-periods">{{ totalPeriods }}</span>
          </div>
        </div>
      </box>

      <box class="summary-card">
        <div class="card-content">
          <div class="icon-container" style="background-color: var(--green-100)">
            <i class="pi pi-check-circle" style="color: var(--green-500)"></i>
          </div>
          <div class="card-details">
            <span class="card-label">{{ t('Pagamentos efetuados') }}</span>
            <span class="card-value" data-testid="employee-payments-paid-count">{{ paidCount }}</span>
          </div>
        </div>
      </box>

      <box class="summary-card">
        <div class="card-content">
          <div class="icon-container" style="background-color: var(--orange-100)">
            <i class="pi pi-clock" style="color: var(--orange-500)"></i>
          </div>
          <div class="card-details">
            <span class="card-label">{{ t('Pagamentos pendentes') }}</span>
            <span class="card-value" data-testid="employee-payments-pending-count">{{ pendingCount }}</span>
          </div>
        </div>
      </box>

      <box class="summary-card">
        <div class="card-content">
          <div class="icon-container" style="background-color: var(--primary-100)">
            <i class="pi pi-dollar" style="color: var(--primary-500)"></i>
          </div>
          <div class="card-details">
            <span class="card-label">{{ t('Total recebido') }}</span>
            <span class="card-value" data-testid="employee-payments-total-received">{{ n(totalReceived, 'currency') }}</span>
          </div>
        </div>
      </box>
    </div>

    <!-- Payments Table -->
    <box :title="t('Histórico de pagamentos')">
      <EmployeePaymentsTable @view="viewPaymentDetail" />
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useEmployeePayroll } from '@/composables/useEmployeePayroll';
import { permissionsService } from '@/services/permissionsService';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface PaymentRow {
  id: Id;
  period_id?: Id;
  status?: string;
  amount?: number | string;
  [key: string]: unknown;
}

const { t, n } = useI18n();
const router = useRouter();
const { myExecutions } = useEmployeePayroll();

const payments = ref<PaymentRow[]>([]);
const isLoading = ref<boolean>(false);

const totalPeriods = computed<number>(() => {
  const uniquePeriods = new Set(payments.value.map(p => p.period_id));
  return uniquePeriods.size;
});

const paidCount = computed<number>(() => {
  return payments.value.filter(p => p.status === 'paid').length;
});

const pendingCount = computed<number>(() => {
  return payments.value.filter(p => p.status === 'calculated').length;
});

const totalReceived = computed<number>(() => {
  return payments.value
    .filter(p => p.status === 'paid')
    .reduce((sum: number, p) => sum + (parseFloat(String(p.amount ?? '0')) || 0), 0);
});

onMounted(async () => {
  await loadMyPayments();
});

const loadMyPayments = async (): Promise<void> => {
  log('EmployeePaymentsPage', 'loadMyPayments', 'Loading employee payments');

  isLoading.value = true;

  try {
    const userId = permissionsService.getUser()?.sub;
    payments.value = (await myExecutions(userId)) as unknown as PaymentRow[];

    log('EmployeePaymentsPage', 'loadMyPayments', 'Payments loaded', {
      count: payments.value.length
    });
  } catch (error) {
    console.error('Error loading my payments:', error);
  } finally {
    isLoading.value = false;
  }
};

const viewPaymentDetail = (payment: PaymentRow): void => {
  router.push({
    name: 'EmployeePaymentDetail',
    params: { id: payment.id }
  });
};
</script>

<style scoped>
.employee-payments-page {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.summary-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.icon-container {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-container i {
  font-size: 1.75rem;
}

.card-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.card-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.card-value {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-color);
}
</style>
